import { useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileAppliedChips from './MobileAppliedChips';
import MobileDiscoveryUniversityCard from './MobileDiscoveryUniversityCard';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import useMobileLoadMore from './useMobileLoadMore';
import { emptyUniversityFilters, getMobileUniversityResults, universitySortOptions } from './mobileUniversityResults';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';

const emptySort = { field: null, direction: 'asc' };

const MobileUniversityList = ({ universities, cityId, countryId, searchQuery, onSearchChange }) => {
    const [filters, setFilters] = useState(emptyUniversityFilters);
    const [sort, setSort] = useState(emptySort);
    const [draftFilters, setDraftFilters] = useState(emptyUniversityFilters);
    const [draftSort, setDraftSort] = useState(emptySort);
    const [drawer, setDrawer] = useState(null);
    const classifications = useMemo(() => [...new Set(universities
        .map(university => university.classification).filter(Boolean))].sort(), [universities]);
    const results = useMemo(() => getMobileUniversityResults(universities, searchQuery, filters, sort),
        [universities, searchQuery, filters, sort]);
    const pagination = useMobileLoadMore({
        items: results,
        filterKey: JSON.stringify([cityId, countryId, searchQuery, filters, sort]),
        storageKey: 'discovery-city-universities-mobile',
        isMobile: true,
    });
    const openFilters = () => { setDraftFilters(filters); setDrawer('filter'); };
    const openSort = () => { setDraftSort(sort); setDrawer('sort'); };
    const closeDrawer = () => setDrawer(null);
    const chips = [];
    if (filters.classification) chips.push({
        key: 'classification', label: `Classification: ${filters.classification}`, onOpen: openFilters,
        onRemove: () => setFilters(current => ({ ...current, classification: '' })),
    });
    if (filters.website) chips.push({
        key: 'website', label: filters.website === 'yes' ? 'Has website' : 'No website', onOpen: openFilters,
        onRemove: () => setFilters(current => ({ ...current, website: '' })),
    });
    if (sort.field) chips.push({
        key: 'sort', label: universitySortOptions.find(option => option.field === sort.field).label,
        icon: sort.direction === 'asc' ? <FaCaretUp aria-label="Ascending" /> : <FaCaretDown aria-label="Descending" />,
        onOpen: openSort, onRemove: () => setSort(emptySort), removeLabel: 'Clear university sort',
    });
    const hasCriteria = Boolean(searchQuery.trim() || filters.classification || filters.website);
    const isFilter = drawer === 'filter';
    const clearDrawer = () => {
        if (isFilter) { setFilters(emptyUniversityFilters); setDraftFilters(emptyUniversityFilters); }
        else { setSort(emptySort); setDraftSort(emptySort); }
        closeDrawer();
    };
    const applyDrawer = () => {
        if (isFilter) setFilters(draftFilters);
        else setSort(draftSort);
        closeDrawer();
    };

    return (
        <>
            <MobileDiscoverySearchToolbar searchValue={searchQuery} onSearchChange={onSearchChange}
                searchPlaceholder="Search universities..." searchAriaLabel="Search universities"
                actions={[
                    { label: 'Filter universities', icon: icFilter, onClick: openFilters },
                    { label: 'Sort universities', icon: icSort, onClick: openSort },
                ]} />
            <MobileAppliedChips chips={chips} />
            {pagination.totalCount === 0 ? (
                <DiscoveryEmptyState
                    className="discovery-empty-state--mobile-jobs"
                    title={hasCriteria ? 'No matching universities found' : 'No universities found'}
                    description={hasCriteria ? 'Try changing your search or filters.' : 'City universities will be displayed here.'}
                    primaryAction={hasCriteria ? { label: 'Clear search & filters', onClick: () => {
                        onSearchChange(''); setFilters(emptyUniversityFilters);
                    } } : undefined}
                    secondaryAction={hasCriteria ? { label: 'Adjust filters', onClick: openFilters } : undefined}
                />
            ) : (
                <div className="mobile-company-list mobile-university-list">
                    {pagination.mobileItems.map(university => (
                        <MobileDiscoveryUniversityCard key={university._id} university={university} />
                    ))}
                    <MobileLoadMoreFooter visibleCount={pagination.visibleCount} totalCount={pagination.totalCount}
                        itemLabel="universities" onLoadMore={pagination.loadMore}
                        isLoading={pagination.isLoadingMore} hasMore={pagination.hasMore} />
                </div>
            )}
            <Modal show={Boolean(drawer)} onHide={closeDrawer}
                className={`with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen${isFilter ? '' : ' discovery-sort-mobile-modal'}`}>
                <div className="discovery-filter-mobile-panel">
                    <div className="discovery-filter-mobile-panel__header">
                        <Modal.Title>{isFilter ? 'Filter universities' : 'Sort universities'}</Modal.Title>
                        <div className="discovery-filter-mobile-panel__header-actions">
                            <button type="button" className="discovery-filter-mobile-panel__close"
                                onClick={closeDrawer} aria-label="Close university drawer">&times;</button>
                        </div>
                    </div>
                    <Modal.Body>
                        {isFilter ? (
                            <>
                                <Form.Group controlId="university-classification" className="mb-4">
                                    <Form.Label>Classification</Form.Label>
                                    <Form.Select value={draftFilters.classification}
                                        onChange={event => setDraftFilters(current => ({ ...current, classification: event.target.value }))}>
                                        <option value="">All classifications</option>
                                        {classifications.map(value => <option key={value} value={value}>{value}</option>)}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group controlId="university-website">
                                    <Form.Label>Website</Form.Label>
                                    <Form.Select value={draftFilters.website}
                                        onChange={event => setDraftFilters(current => ({ ...current, website: event.target.value }))}>
                                        <option value="">All</option>
                                        <option value="yes">Has website</option>
                                        <option value="no">No website</option>
                                    </Form.Select>
                                </Form.Group>
                            </>
                        ) : (
                            <div className="discovery-sort-mobile-options" role="radiogroup" aria-label="Sort universities by">
                                {universitySortOptions.map(option => {
                                    const active = draftSort.field === option.field;
                                    return (
                                        <button key={option.field} type="button" role="radio" aria-checked={active}
                                            className={`discovery-sort-mobile-option${active ? ' is-active' : ''}`}
                                            onClick={() => setDraftSort({ field: option.field,
                                                direction: active && draftSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                            <span className="discovery-sort-mobile-option__label">
                                                <span className="discovery-sort-mobile-option__radio" aria-hidden="true" />
                                                <span>{option.label}</span>
                                            </span>
                                            {active && <span className="discovery-sort-mobile-option__direction">
                                                <span>{draftSort.direction === 'asc' ? 'Ascending' : 'Descending'}</span>
                                                {draftSort.direction === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
                                            </span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </Modal.Body>
                    <div className="discovery-filter-mobile-panel__footer">
                        <Button variant="outline-secondary" className="discovery-filter-mobile-panel__reset" onClick={clearDrawer}>
                            {isFilter ? 'Clear all' : 'Clear sort'}
                        </Button>
                        <Button variant="primary" className="discovery-filter-mobile-panel__apply" onClick={applyDrawer}>
                            {isFilter ? 'Apply' : 'Apply sort'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default MobileUniversityList;
